import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import {
  CallOffMechanism,
  Currency,
  useGetFrameworkAgreementQuery,
  useListFrameworkCallOffsQuery,
} from "../../../autogen/bff-api";
import { EntityViewSkeleton } from "../../../common/EntityViewSkeleton";
import { HorizontalScrollBox } from "../../../common/HorizontalScrollBox";
import { isForbiddenError } from "../../../common/errors/isNoAccessError";
import { useApiError } from "../../../common/errors/useApiError";
import { CallOffsCutOffNote, CallOffsEmptyState, CallOffsTable } from "../../framework-agreement/CallOffsSection";
import { SectionCard } from "../../framework-agreement/SectionCard";

export const SupplierCallOffs = ({
  frameworkAgreementId,
  contractId,
}: {
  frameworkAgreementId: string;
  contractId: string;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const framework = useGetFrameworkAgreementQuery({ frameworkAgreementId });
  const supplier = (framework.data?.panel ?? []).find((member) => member.contractId === contractId);
  const unregistered = (framework.data?.unregisteredSuppliers ?? []).some((member) => member.contractId === contractId);
  const callOffs = useListFrameworkCallOffsQuery(
    supplier ? { frameworkAgreementId, supplierOrganizationId: supplier.organizationId } : skipToken,
  );

  const forbidden = [framework.error, callOffs.error].some((error) => error != null && isForbiddenError(error));
  if (forbidden) return null;

  const body = () => {
    if (framework.error) return displayer.show(framework.error);
    if (callOffs.error) return displayer.show(callOffs.error);
    if (framework.isLoading || callOffs.isLoading || !framework.data) return <EntityViewSkeleton />;
    if (!supplier && unregistered) {
      return (
        <CallOffsEmptyState
          message={t(
            "This supplier is on the framework agreement's panel but isn't registered in SourceMagnet yet. It is invited to mini-competitions by e-mail, and can get call-offs once it has registered.",
          )}
        />
      );
    }
    if (!supplier) return <CallOffsEmptyState message={t("This contract is not on the framework agreement's panel")} />;
    if (!callOffs.data) return <EntityViewSkeleton />;
    const rows = callOffs.data.callOffs ?? [];
    if (rows.length === 0) return <CallOffsEmptyState message={t("No call-offs have been made with this supplier yet")} />;
    return (
      <>
        <HorizontalScrollBox>
          <CallOffsTable
            callOffs={rows}
            currency={framework.data.ceilingValue?.currency ?? Currency.Nok}
            showsMechanism={framework.data.callOffMechanism === CallOffMechanism.DirectAwardAndMiniCompetition}
            showsSupplier={false}
          />
        </HorizontalScrollBox>
        <CallOffsCutOffNote shown={rows.length} total={callOffs.data.totalCount} />
      </>
    );
  };

  return <SectionCard title={t("Call-offs")}>{body()}</SectionCard>;
};

export const CallOffsNotShown = () => {
  const { t } = useTranslation();
  return (
    <SectionCard title={t("Call-offs")}>
      <CallOffsEmptyState message={t("You do not have access to the call-offs under this framework agreement.")} />
    </SectionCard>
  );
};
